<template>
    <div :data-cy="isFreeGift(lineItem) ? 'free-gift-container' : 'item-container'">
        <div v-if="!isCouponOrFreeGift(lineItem)" class="avatar indicator mr-3">
            <span v-if="!isInteractive" class="indicator-item indicator-top badge border rounded-full border-secondary bg-primary text-xs text-base-100 text-center px-2">
                {{ lineItem.quantity }} x
            </span>
            <div
                v-if="excentosConfiguration != null"
                class="border-base-300"
                :class="{ 'w-12 h-12': !isInteractive, 'w-20 h-20': isInteractive }"
            >
                <NuxtImg
                    :src="lineItemImageUrl"
                    :alt="lineItem.cover?.alt"
                    :width="isInteractive ? '80' : '48'"
                    :height="isInteractive ? '80' : '48'"
                    quality="60"
                    loading="lazy"
                    :placeholder="'/images/loader.gif'"
                />
            </div>
            <MiscLink
                v-else
                :to="productLink"
                class="h-auto border-base-300"
                :class="{ 'w-12': !isInteractive, 'w-20': isInteractive }"
            >
                <NuxtImg
                    :src="lineItemImageUrl"
                    :alt="lineItem.cover?.alt"
                    :width="isInteractive ? '80' : '48'"
                    :height="isInteractive ? '80' : '48'"
                    quality="60"
                    loading="lazy"
                    :placeholder="'/images/loader.gif'"
                />
            </MiscLink>
        </div>
        <div v-else-if="isFreeGift(lineItem)" class="avatar indicator mr-3">
            <span v-if="!isInteractive" class="indicator-item indicator-top badge border rounded-full border-secondary bg-primary text-xs text-base-100 text-center px-2">
                {{ lineItem.quantity }} x
            </span>
            <div class="h-auto border-base-300" :class="{ 'w-12': !isInteractive, 'w-20': isInteractive }">
                <NuxtImg
                    :src="lineItemImageUrl"
                    :alt="lineItem.cover?.alt"
                    :width="isInteractive ? '80' : '48'"
                    :height="isInteractive ? '80' : '48'"
                    quality="60"
                    loading="lazy"
                    :placeholder="'/images/loader.gif'"
                />
            </div>
        </div>
        <div v-else class="mr-4 bg-base-100">
            <div class="flex justify-center items-center" :class="{ 'w-12': !isInteractive,'w-20': isInteractive }" />
        </div>
        <div
            class="cart-list-item w-full flex flex-col justify-between border-b border-base-300"
            :class="{
                'pb-6': !isInteractive
            }"
        >
            <div :class="{'flex gap-3 justify-between': !isInteractive, 'lg:flex lg:justify-between': !mobileOnly }">
                <div>
                    <div
                        class="w-full flex gap-2 items-start"
                        :class="{
                            'flex-row-reverse justify-between': !isInteractive,
                            'lg:justify-end': isInteractive,
                            'lg:flex-row-reverse': !mobileOnly
                        }"
                    >
                        <div
                            v-if="lineItem?.price?.listPrice?.percentage"
                            class="badge badge-sale rounded-full min-w-12"
                        >
                            - {{ roundedPercentage(lineItem?.price?.listPrice?.percentage) }} %
                        </div>
                        <span
                            v-if="isCouponOrFreeGift(lineItem)"
                            class="w-full shrink-1 text-sm font-semibold leading-5 overflow-hidden text-ellipsis text-neutral"
                            data-cy="free-gift-or-coupon-item-name"
                        >
                            {{ displayLabel }}
                        </span>
                        <MiscLink
                            v-else
                            :to="productLink"
                            class="w-full shrink-1 text-sm font-semibold leading-5 overflow-hidden text-ellipsis text-base-content"
                            data-cy="item-name"
                        >
                            <template v-if="excentosConfiguration != null">
                                {{ excentosConfiguration?.['Name'] }}
                            </template>
                            <template v-else>
                                {{ lineItem.label }}
                            </template>
                        </MiscLink>
                    </div>
                    <div v-if="lineItem.payload?.productNumber != null" class="text-sm leading-4 text-base-content-subtle font-normal mt-1">
                        {{ `${$t('cart.list.articleNumber')} ${lineItem.payload?.productNumber}` }}
                    </div>
                    <div v-if="lineItem.payload?.code != null && lineItem.payload?.code.trim() !== '' && !isFreeGift(lineItem)" class="text-sm leading-4 text-base-content-subtle normal mt-1">
                        {{ `${$t('cart.list.voucherCode')}: ${lineItem.payload?.code}` }}
                    </div>
                    <div v-if="lineItem.payload?.leftAmount != null" class="text-sm leading-4 text-primary mt-1">
                        {{ `${$t('cart.list.voucherBalance')}: ${getFormattedPrice(lineItem.payload?.leftAmount, { forceFraction: true })}` }}
                    </div>
                    <div v-if="courseframeCourseName != null" class="text-sm leading-4 text-base-content-subtle font-normal mt-1" data-cy="courseframe-cart-course">
                        {{ `${$t('courseframe.cart.courseLabel')}: ${courseframeCourseName}` }}
                    </div>
                    <div v-if="lineItem.payload?.courseframe?.courseFreeText" class="text-sm leading-4 text-base-content-subtle font-normal mt-1" data-cy="courseframe-cart-personalization">
                        {{ `${$t('courseframe.cart.personalizationLabel')}: ${lineItem.payload?.courseframe?.courseFreeText}` }}
                    </div>

                    <div v-if="lineItem.payload?.options?.length > 0 || excentosConfiguration != null" class="flex flex-row flex-wrap gap-1 mt-1" :class="{ 'lg:mb-9': isInteractive && !mobileOnly && warrantyData == null}">
                        <template v-if="excentosConfiguration != null">
                            <template
                                v-for="(attribute, excentosConfigIndex) in Object.keys(excentosConfiguration)"
                                :key="excentosConfigIndex"
                            >
                                <span
                                    v-if="!excentosKeyBlacklist.includes(attribute)"
                                    class="text-sm leading-4 font-medium text-base-content-subtle">{{ attribute.replaceAll('_', ' ') }}: {{ excentosConfiguration[attribute] }}<span v-if="excentosConfigIndex < Object.keys(excentosConfiguration)?.length - 1">,</span></span>
                            </template>
                        </template>
                        <template
                            v-for="(option, optionIndex) in lineItem.payload?.options"
                            v-else
                            :key="optionIndex"
                        >
                            <template v-if="option.option === 'Yes' || option.option === 'Ja'">
                                <div class="text-sm leading-4" :class="option.group === 'Right/left' ? 'font-semibold text-neutral': 'font-normal text-base-content-subtle'">
                                    {{ option.group }}<span v-if="optionIndex < lineItem?.payload?.options.length - 1">,</span>
                                </div>
                            </template>

                            <template v-else-if="option.option !== 'Yes' && option.option !== 'Ja' && option.option !== 'No' && option.option !== 'Nein'">
                                <div class="text-sm leading-4" :class="option.group === 'Right/left' ? 'font-semibold text-neutral': 'font-normal text-base-content-subtle'">
                                    {{ option.option }}<span v-if="optionIndex < lineItem?.payload?.options.length - 1">,</span>
                                </div>
                            </template>
                        </template>
                    </div>

                    <LazyProductWarrantyLabelCompact
                        v-if="warrantyData != null && !isCouponOrFreeGift(lineItem)"
                        :warranty-data="warrantyData"
                        :product-id="lineItem.referencedId!"
                        class="mt-4"
                        :class="{ 'lg:mb-9': isInteractive }"
                    />

                </div>
                <div
                    v-if="lineItem?.price?.unitPrice != null"
                    class="w-min flex text-sm leading-5 text-left"
                    :class="{
                        'mt-4 self-end': isInteractive,
                        'mt-0 justify-end': !isInteractive,
                        'lg:mt-0 lg:flex lg:flex-row lg:justify-end lg:self-start': !mobileOnly
                    }"
                >
                    <div class="text-neutral font-semibold shrink-0" :class="{ 'lg:flex lg:gap-2 lg:justify-end': !mobileOnly }">
                        <div class="leading-tight" :class="{ 'text-sale': lineItem.price.listPrice?.price, 'lg:flex lg:flex-col lg:items-end': !mobileOnly }">
                            <span data-cy="cartListItemPrice" class="text-sm">
                                <template v-if="isFreeGift(lineItem)">
                                    <span class="text-new-focus uppercase">
                                        {{ $t('cart.list.freeItem') }}
                                    </span>
                                </template>
                                <template v-else-if="lineItem.type === 'promotion' && (lineItem.price?.totalPrice === 0 || lineItem.price?.unitPrice === 0)" />
                                <template v-else-if="lineItem?.price?.unitPrice < 0">
                                    - {{ getFormattedPrice(lineItem?.price?.unitPrice * -1, { forceFraction: true }) }}
                                </template>
                                <template v-else-if="lineItem?.price?.unitPrice > 0 && lineItem.quantity > 1">
                                    {{ getFormattedPrice(lineItem?.price?.totalPrice, { forceFraction: true }) }}
                                </template>
                                <template v-else-if="lineItem.payload?.discountScope != 'delivery'">
                                    {{ getFormattedPrice(lineItem?.price?.unitPrice, { forceFraction: true }) }}
                                </template>
                            </span>

                            <div
                                v-if="lineItem.price.listPrice?.price && lineItem?.price?.listPrice?.price > 0"
                                :class="{ 'line-through text-sm text-base-content-subtle font-normal mt-1': lineItem.price.listPrice?.price }"
                                class="text-neutral"
                            >
                                <template v-if="lineItem.quantity > 1">
                                    {{ getFormattedPrice(lineItem?.price?.listPrice?.price * lineItem.quantity, { forceFraction: true }) }}
                                </template>
                                <template v-else>
                                    {{ getFormattedPrice(lineItem?.price?.listPrice?.price, { forceFraction: true }) }}
                                </template>
                            </div>
                            
                            <div
                                v-if="lineItem.quantity > 1"
                                data-cy="cartListItemPriceEach"
                                class="text-sm font-bold mt-1 text-base-content-subtle leading-4"
                            >
                                {{ $t('cart.list.priceEach', { price: getFormattedPrice(lineItem?.price?.unitPrice, { forceFraction: true }) }) }}
                            </div>
                        </div>
                        <button
                            v-if="isInteractive"
                            type="button"
                            class="hidden btn btn-ghost btn-circle bg-transparent border-none shadow-none"
                            :class="{ 'lg:flex lg:-mt-3': !mobileOnly }"
                            data-cy="removeLineItem"
                            :disabled="isCartWriteInFlight"
                            :aria-label="$t('cart.list.removeFromCart')"
                            @click="onRemoveLineItem(lineItem)"
                        >
                            <MiscIcon name="trash" />
                        </button>
                    </div>
                </div>
            </div>
            <div
                v-if="isInteractive"
                class="flex items-center justify-between mb-8 mt-4"
                :class="{
                    'lg:justify-start lg:gap-4 lg:mt-1': !mobileOnly
                }"
            >
                <div v-if="!isCoupon(lineItem.type)" class="relative form-control flex flex-row gap-3">
                    <div class="relative" data-cy="qty-selector-cart">
                        <ProductQtySelector
                            :qty="qty"
                            :min="1"
                            :max="lineItem.payload?.isCloseout ? lineItem.payload?.stock != null && typeof lineItem.payload?.stock === 'number' ? lineItem.payload?.stock : 0 : 100"
                            data-cy="cartListQty"
                            :input-disabled="isFreeGift(lineItem)"
                            @update:qty="qty = $event"
                        />
                        <span v-if="loading" class="loading w-5 h-5 absolute right-0 left-0 top-0 bottom-0 m-auto" />
                    </div>
                </div>
                <div class="flex items-center" :class="{ 'lg:gap-2': !mobileOnly, 'ml-auto': isCoupon(lineItem.type) }">
                    <div v-if="!isCouponOrFreeGift(lineItem)" class="wishlist-button flex items-center text-primary">
                        <WishlistProductToggleButton
                            class="wishlist-button btn-outline px-3 flex flex-row"
                            :class="{ 'lg:pr-0': !mobileOnly }"
                            :product="{ id: lineItem.referencedId! }"
                        >
                            <span class="hidden" :class="{ 'lg:block': !mobileOnly }">
                                {{ $t('cart.list.save') }}
                            </span>
                        </WishlistProductToggleButton>
                    </div>
                    <button
                        v-if="isInteractive"
                        type="button"
                        class="btn btn-ghost btn-circle bg-transparent border-none text-primary shadow-none"
                        :class="{ 'lg:hidden': !mobileOnly }"
                        data-cy="removeLineItem"
                        :disabled="isCartWriteInFlight"
                        :aria-label="$t('cart.list.removeFromCart')"
                        @click="onRemoveLineItem(lineItem)"
                    >
                        <MiscIcon name="trash" class="w-5 h-5" />
                    </button>
                </div>
            </div>
            <div
                v-if="lineItem.payload?.badges?.multiDeal || lineItem.payload?.badges?.promotion"
                :class="{
                    '-mt-4 mb-7': isInteractive,
                    'mt-1': !isInteractive,
                }"
            >
                <div v-if="lineItem.payload?.badges?.multiDeal" class="badge badge-accent px-3 leading-5">
                    {{ $t('cart.list.badgeMultiDeal') }}
                </div>
                <div v-if="lineItem.payload?.badges?.promotion" class="badge badge-sale px-3 leading-5">
                    {{ $t('cart.list.badgePromotion') }}
                </div>
                <div v-if="lineItem.payload?.promotion?.discountValue" class="text-sm leading-6 text-base-content-subtle font-normal mt-1">
                    {{ $t('cart.list.youSave', { price: getFormattedPrice(lineItem.payload?.promotion?.discountValue, { forceFraction: true }) }) }}
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import type { Schemas } from '#shopware'

interface CartListItemProps {
    lineItem: Schemas['LineItem'],
    isInteractive?: boolean,
    mobileOnly?: boolean,
    addToCartSource?: 'cart' | 'added_cart_layer',
}

const props = withDefaults(defineProps<CartListItemProps>(), {
    isInteractive: true,
    mobileOnly: false,
    addToCartSource: 'cart',
})

const { t, localeProperties } = useI18n()
const { getFormattedPrice } = usePrice()
const { showNotification } = useNotification()
const loading = ref(false)

const { cart, removeItem, changeProductQuantity, isCartWriteInFlight } = useCart()
const nuxtApp = useNuxtApp()

const { getWarrantyData } = useCartWarrantyLabels()
const warrantyData = computed(() => getWarrantyData(props.lineItem.referencedId))

const qty = ref(props.lineItem.quantity)
let qtyTimeout: ReturnType<typeof setTimeout>

const excentosKeyBlacklist = EXCENTOS_DISPLAY_KEY_BLACKLIST
const excentosConfiguration = computed(() => parseExcentosConfiguration(props.lineItem.payload?.custom_cart_excentos_configuration))

const productLink = computed(() => {
    const languageId = localeProperties.value.localeId as string | undefined
    const seoPathInfo = languageId != null ? props.lineItem.extensions?.a4gSeoUrls?.urls?.[languageId] : undefined

    return seoPathInfo ? `/${seoPathInfo}` : `/detail/${props.lineItem.referencedId}`
})

// courseName only exists for a course matched in the backend catalog; otherwise the customer's own
// search term stands in. Print-text-only products carry neither.
const courseframeCourseName = computed(() => {
    return props.lineItem.payload?.courseframe?.courseName ?? props.lineItem.payload?.courseframe?.searchTerm ?? null
})

const lineItemImageUrl = computed(() => {
    return props.lineItem.payload?.courseframe?.imageUrl ?? props.lineItem.cover?.url
})

watch(qty, async (value, oldValue) => {
    clearTimeout(qtyTimeout)

    if (value !== oldValue) {
        qtyTimeout = setTimeout(async () => {
            await updateQty(props.lineItem, value)
        }, 1000)
    }
})

// Without this a pending qty write fires against a line item that is already gone.
onUnmounted(() => clearTimeout(qtyTimeout))

async function onRemoveLineItem (lineItem: Schemas['LineItem']) {
    // A quantity change is debounced by 1s (see the qty watcher). Removing the line item
    // makes a pending update moot, and letting it fire afterwards is producing a 409 "cart is locked" collision.
    clearTimeout(qtyTimeout)

    await removeItem(lineItem)

    if (cart.value?.errors) {
        const errorList = Array.isArray(cart.value.errors)
            ? cart.value.errors
            : Object.values(cart.value.errors)
        if (errorList.length <= 0) {
            showNotification(t('cart.list.removeSuccess'),'success')
            nuxtApp.callHook('removeFromCart', { product: lineItem, action: props.addToCartSource })
        }
    }
}

async function updateQty (lineItem: Schemas['LineItem'], quantity: number) {
    if (
        (lineItem.quantityInformation?.maxPurchase != null && quantity > lineItem.quantityInformation?.maxPurchase) ||
        (lineItem.payload?.isCloseout && quantity > lineItem.payload?.stock)
    ) {
        let maxQty
        if (lineItem.quantityInformation?.maxPurchase != null && quantity > lineItem.quantityInformation?.maxPurchase) {
            maxQty = lineItem.quantityInformation?.maxPurchase
        } else if (lineItem.payload?.isCloseout && quantity > lineItem.payload?.stock) {
            maxQty = lineItem.payload?.stock
        }

        qty.value = maxQty != null ? maxQty : 1

        showNotification(
            t('cart.list.updateInvalidQty', {
                productName: lineItem.label,
                qty: maxQty
            }),
            'error'
        )
        return
    }

    loading.value = true

    if (quantity <= 0) {
        await onRemoveLineItem(lineItem)
        loading.value = false
        return
    }

    const oldQty = lineItem.quantity ?? 1
    await changeProductQuantity({ id: lineItem.id, quantity })

    if (cart.value?.errors) {
        const errorList = Array.isArray(cart.value.errors)
            ? cart.value.errors
            : Object.values(cart.value.errors)
        if (errorList.length <= 0) {
            if (quantity > oldQty) {
                nuxtApp.callHook('addToCart', { addQty: quantity - oldQty, product: lineItem, action: props.addToCartSource })
            } else {
                nuxtApp.callHook('removeFromCart', { qty: Math.abs(quantity - oldQty), product: lineItem, action: props.addToCartSource })
            }
        }
    }

    loading.value = false
}

function roundedPercentage(percentage: number) {
    return Math.ceil(percentage)
}

function isCoupon (type: string | undefined) {
    return type === 'promotion' || type === 'easy-coupon'
}

function isFreeGift (lineItem: Schemas['LineItem']) {
    const payloadType = (lineItem.payload as Record<string, unknown> | undefined)?.line_item_type
    return payloadType === "promotion-gift-wrapper"
}

function isCouponOrFreeGift (lineItem: Schemas['LineItem']) {
    return isCoupon(lineItem.type) || isFreeGift(lineItem)
}

const displayLabel = computed(() => {
    if (isFreeGift(props.lineItem) && props.isInteractive && props.lineItem.quantity > 1) {
        return t('cart.list.freeGiftQuantityName', { qty: props.lineItem.quantity, name: props.lineItem.label })
    }

    return props.lineItem.label
})
</script>
